import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Phone, ArrowRight, ChevronDown, Plus, X,
  Check, ArrowUpRight
} from 'lucide-react';

// Simple fade-in wrapper
const FadeIn = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] }}
    viewport={{ once: true, margin: "-50px" }}
    className={className}
  >
    {children}
  </motion.div>
);

// Countdown timer
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate - now;
      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex gap-3 sm:gap-4 justify-center">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="text-center">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 py-2 sm:px-5 sm:py-3 min-w-[56px] sm:min-w-[72px]">
            <div className="text-xl sm:text-3xl font-serif font-light text-white tabular-nums">{String(value).padStart(2, '0')}</div>
            <div className="text-white/60 text-[10px] sm:text-xs uppercase tracking-widest mt-0.5">{unit}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Accordion item for "What's Included"
const AccordionItem = ({ item, isOpen, onToggle }) => (
  <div className="border-b border-stone-200 last:border-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 sm:py-6 text-left group"
    >
      <div className="flex items-center gap-4 pr-4">
        <span className="text-lg sm:text-xl font-serif text-stone-800 group-hover:text-sage-700 transition-colors">
          {item.title}
        </span>
        <span className="text-sm text-stone-400 hidden sm:inline">{item.subtitle}</span>
      </div>
      <motion.div
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.2 }}
        className="text-stone-400 flex-shrink-0"
      >
        <Plus className="w-5 h-5" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <div className="pb-6 pr-8">
            <p className="text-stone-600 leading-relaxed mb-4 text-sm sm:text-base">
              {item.description}
            </p>
            {item.features && (
              <ul className="space-y-2">
                {item.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-stone-500">
                    <Check className="w-4 h-4 text-sage-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// Photo Gallery
const PhotoGallery = () => {
  const images = [
    "https://i.ibb.co/3mQwhwH6/Whats-App-Image-2025-10-13-at-13-36-29.jpg",
    "https://i.ibb.co/S7wG7N11/Whats-App-Image-2025-10-13-at-13-34-53.jpg",
    "https://i.ibb.co/gLXGWkWj/Whats-App-Image-2025-10-13-at-13-34-53-1.jpg",
    "https://i.ibb.co/1fFSbqTw/Whats-App-Image-2025-10-13-at-13-34-50.jpg",
    "https://i.ibb.co/V0WF4vvT/Whats-App-Image-2025-10-13-at-13-34-50-1.jpg",
    "https://i.ibb.co/gKZGrqD/Whats-App-Image-2025-10-13-at-13-34-49.jpg",
    "https://i.ibb.co/wFrW01HG/Whats-App-Image-2025-10-13-at-13-34-49-1.jpg",
    "https://i.ibb.co/PsT5787r/Whats-App-Image-2025-10-13-at-13-34-44.jpg",
    "https://i.ibb.co/Fq8CLDBX/Whats-App-Image-2025-10-13-at-13-41-02.jpg",
    "https://i.ibb.co/SDGprL3c/Whats-App-Image-2025-10-13-at-13-41-02-2.jpg",
    "https://i.ibb.co/GfxvXC07/Whats-App-Image-2025-10-13-at-13-41-02-1.jpg",
    "https://i.ibb.co/q3LJXrty/Whats-App-Image-2025-10-13-at-13-41-01.jpg",
    "https://i.ibb.co/3YFS8TVV/Whats-App-Image-2025-10-13-at-13-41-01-4.jpg",
    "https://i.ibb.co/twbnT9Cn/Whats-App-Image-2025-10-13-at-13-41-01-3.jpg",
    "https://i.ibb.co/Kz8BgWXc/Whats-App-Image-2025-10-13-at-13-41-01-2.jpg",
    "https://i.ibb.co/Mx3Hxf5g/Whats-App-Image-2025-10-13-at-13-41-01-1.jpg",
    "https://i.ibb.co/SDXwvCfn/Whats-App-Image-2025-10-13-at-13-40-59.jpg",
    "https://i.ibb.co/RTY2cChW/Whats-App-Image-2025-10-13-at-13-40-58.jpg",
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <section className="py-20 sm:py-28 bg-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn className="text-center mb-12 sm:mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-sage-600 mb-4">Gallery</p>
          <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
            Moments from Past Retreats
          </h2>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="relative rounded-xl overflow-hidden bg-stone-100 aspect-[16/10] sm:aspect-[2/1]">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentIndex}
                src={images[currentIndex]}
                alt={`Retreat moment ${currentIndex + 1}`}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setIsFullscreen(true)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              />
            </AnimatePresence>

            <button
              onClick={prevImage}
              className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2.5 rounded-full shadow-sm transition-all"
            >
              <ArrowRight className="w-4 h-4 text-stone-700 rotate-180" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2.5 rounded-full shadow-sm transition-all"
            >
              <ArrowRight className="w-4 h-4 text-stone-700" />
            </button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-stone-900/60 backdrop-blur-sm text-white/90 px-3 py-1.5 rounded-full text-xs tracking-wide">
              {currentIndex + 1} / {images.length}
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="mt-4 overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 pb-1">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`relative flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-md overflow-hidden transition-all ${
                    index === currentIndex
                      ? 'ring-2 ring-sage-600 opacity-100'
                      : 'opacity-50 hover:opacity-80'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </FadeIn>
      </div>

      {/* Fullscreen overlay */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setIsFullscreen(false)}
          >
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <img
              src={images[currentIndex]}
              alt=""
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full"
            >
              <ArrowRight className="w-5 h-5 text-white rotate-180" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full"
            >
              <ArrowRight className="w-5 h-5 text-white" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Testimonial card
const TestimonialCard = ({ name, text, featured = false, delay = 0 }) => (
  <FadeIn delay={delay} className="break-inside-avoid mb-5">
    <div className={`rounded-xl p-6 sm:p-7 border transition-colors ${
      featured
        ? 'bg-sage-50 border-sage-200'
        : 'bg-white border-stone-100 shadow-sm'
    }`}>
      <span className={`block font-serif leading-none mb-3 select-none ${
        featured ? 'text-4xl text-sage-300' : 'text-3xl text-stone-200'
      }`} aria-hidden="true">&ldquo;</span>
      <blockquote className={`leading-relaxed text-sm sm:text-base mb-5 italic ${
        featured ? 'text-stone-700' : 'text-stone-600'
      }`}>
        {text}
      </blockquote>
      <div className="flex items-center gap-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
          featured ? 'bg-sage-200' : 'bg-sage-100'
        }`}>
          <span className={`text-xs font-medium ${featured ? 'text-sage-800' : 'text-sage-700'}`}>
            {name[0]}
          </span>
        </div>
        <span className="text-stone-700 font-medium text-sm">{name}</span>
      </div>
    </div>
  </FadeIn>
);


// Testimonials section with masonry layout + show-more
const allTestimonials = [
  {
    name: "Jannah",
    text: "It was a blessing to be a part of this first Beshno retreat. To meet and start to know the beautiful, servant-hearted souls of the Beshno community, to begin learning of the Sufi way and share the week-end with them in this place of peace in nature. Danish exudes such humility, kindness and peace — in Rumi's words, he is one of those who obeys out of love.",
    featured: true,
  },
  {
    name: "Somia",
    text: "BESHNO retreat was a great and soul fulfilled experience. I loved every moment. Connecting with others and learning what the essence of Sufism is. Understanding how to connect with God and better my outlook in everything I do. I would recommend anyone to go experience this beautiful retreat for themselves — go with an open heart, an open mind and willingness to understand and learn without judgment. Thank you Danish. I will be back insha'allah.",
    featured: true,
  },
  {
    name: "Sabrina",
    text: "This retreat was a full heart connection opportunity as well as a beginning of something new in me. Come together as human beings, learning more about ourselves and other ways to live spirituality. I am really thankful for the generosity, the humility, the laughs, the humor, the openness, the deep listening and loving presence.",
    featured: true,
  },
  {
    name: "Khalid Maaroub",
    text: "An excellent retreat to taste the flavor of Sufism in a beautiful place with wonderful company.",
    featured: false,
  },
  {
    name: "Hannah",
    text: "I found it to be a completely enlightening experience. It was a privilege to learn about the essence of Sufism and be welcomed into such a beautiful universe with open arms. I left the retreat more connected to my true nature and inspired to bring this connection into my daily life.",
    featured: false,
  },
  {
    name: "Beata",
    text: "It was incredible experience to keep in your heart for a lifetime.",
    featured: false,
  },
  {
    name: "Naeem",
    text: "I didn't know too much about Sufiism, but my gut feeling told me that joining this retreat would increase my spirituality and give me the realignment that I needed, and this retreat gave me exactly that. Even though the retreat only lasted for 2-3 days, I feel that I've come out with new perspectives and a new skill set of looking inward, to realign with my values and assess the conditions of my heart.",
    featured: false,
  },
  {
    name: "Fin",
    text: "This was my first exposure to a Sufi Retreat. It was a wonderful, warm, energetic experience. The people who were drawn there were a big broad spread of nationalities and backgrounds. All brought with them love and calm. I was very interested to learn and explore but the energy of the people was a true gift. The rustic nature in the locations was beautiful. The Yoga was grounding and left me with the feeling that I wanted more. Definitely a special time.",
    featured: false,
  },
  {
    name: "Amine",
    text: "I felt called to this retreat and the experience did not disappoint. I'm glad I was there.",
    featured: false,
  },
  {
    name: "Alex",
    text: "What a beautiful, heartfelt space and experience. To have the chance to embody the Sufi teaching and explore in community was truly special, I highly recommend.",
    featured: false,
  },
  {
    name: "Camille",
    text: "The real strength of this retreat is the facilitator, Danish, and his undeniable calm presence. He has attracted beautiful people to come together in a serene, peaceful and natural space.",
    featured: false,
  },
];

// Distribute testimonials across 3 columns for a masonry effect
const distributeIntoColumns = (items, numCols) => {
  const cols = Array.from({ length: numCols }, () => []);
  items.forEach((item, i) => cols[i % numCols].push({ ...item, originalIndex: i }));
  return cols;
};

const TestimonialsSection = () => {
  const [showAll, setShowAll] = useState(false);

  const initialCount = 6;
  const visibleTestimonials = showAll ? allTestimonials : allTestimonials.slice(0, initialCount);

  // Desktop: 3 columns; mobile handled by single-column CSS
  const desktopCols = distributeIntoColumns(visibleTestimonials, 3);

  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn className="text-center mb-14 sm:mb-20">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-sage-600 mb-4">Voices</p>
          <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
            What past guests say
          </h2>
        </FadeIn>

        {/* Desktop 3-column masonry */}
        <div className="hidden md:grid md:grid-cols-3 md:gap-5">
          {desktopCols.map((col, colIdx) => (
            <div key={colIdx}>
              {col.map((t) => (
                <TestimonialCard
                  key={t.name + t.originalIndex}
                  name={t.name}
                  text={t.text}
                  featured={t.featured}
                  delay={t.originalIndex * 0.06}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Mobile single-column */}
        <div className="md:hidden flex flex-col gap-4">
          {visibleTestimonials.map((t, i) => (
            <TestimonialCard
              key={t.name + i}
              name={t.name}
              text={t.text}
              featured={t.featured}
              delay={i * 0.05}
            />
          ))}
        </div>

        {/* Show more / show less */}
        {!showAll && allTestimonials.length > initialCount && (
          <FadeIn delay={0.2} className="mt-10 text-center">
            <button
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 text-stone-600 border border-stone-200 hover:border-stone-400 hover:text-stone-800 px-7 py-3 rounded-full text-sm transition-all"
            >
              Show more voices
              <ChevronDown className="w-4 h-4" />
            </button>
          </FadeIn>
        )}

        {showAll && (
          <FadeIn delay={0} className="mt-10 text-center">
            <button
              onClick={() => setShowAll(false)}
              className="inline-flex items-center gap-2 text-stone-400 hover:text-stone-600 text-sm transition-colors"
            >
              <ChevronDown className="w-4 h-4 rotate-180" />
              Show fewer
            </button>
          </FadeIn>
        )}
      </div>
    </section>
  );
};


// ─── MAIN COMPONENT ────────────────────────────────────────────

const WeekendRetreat = () => {
  const nextRetreatDate = new Date('2026-04-17T00:00:00').getTime();
  const [openAccordion, setOpenAccordion] = useState(null);

  const includedItems = [
    {
      title: "Accommodation",
      subtitle: "2-night countryside estate",
      description: "Stay in a serene countryside estate near Barcelona, featuring shared dormitory rooms with rustic charm, surrounded by lush gardens and a sacred 1000+ year old tree.",
      features: ["Shared dormitory rooms", "Beautiful gardens", "Sacred 1000+ year old tree", "Peaceful natural setting"]
    },
    {
      title: "All Meals",
      subtitle: "Nourishing vegetarian cuisine",
      description: "All vegetarian meals thoughtfully prepared to support your spiritual journey. Experience Sufi cooking as a meditation practice, with fresh, local ingredients.",
      features: ["Fresh vegetarian meals", "Sufi cooking workshop", "Local organic ingredients", "Mindful eating practices"]
    },
    {
      title: "Expert Guidance",
      subtitle: "Authentic Sufi practitioners",
      description: "Learn directly from Abd al-Wadud Sabaté and Danish Suhail, respected practitioners carrying centuries of unbroken Sufi wisdom.",
      features: ["Two authentic practitioners", "Decades of experience", "Unbroken lineage", "Personal guidance"]
    },
    {
      title: "Sacred Practices",
      subtitle: "Complete spiritual toolkit",
      description: "Guided muraqaba meditation, nature trek, Sufi yoga, live Sufi music, hadrah circles, and integration workshops.",
      features: ["Muraqaba meditation", "Guided nature trek", "Sufi yoga practice", "Live Sufi music", "Hadrah circles"]
    },
    {
      title: "Community",
      subtitle: "Like-minded seekers",
      description: "Connect with other carefully selected participants. Form deep connections through group sharing, collective practices, and shared meals.",
      features: ["Small intimate group", "Group sharing circles", "Collective practices", "Supportive community"]
    },
    {
      title: "Integration",
      subtitle: "Lasting transformation tools",
      description: "Structured integration workshop, daily journaling prompts, and ongoing support to help you maintain your spiritual growth at home.",
      features: ["Integration workshop", "Journaling prompts", "Take-home practices", "Ongoing support"]
    }
  ];

  return (
    <div className="font-sans antialiased bg-cream text-stone-800 overflow-x-hidden">

      {/* ─── HERO ──────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=2071&auto=format&fit=crop"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/65" />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl mx-auto">
          {/* Logo */}
          <motion.img
            src="/beshnologo.png"
            alt="Beshno Retreats"
            className="h-12 sm:h-20 w-auto mx-auto mb-10 sm:mb-14 brightness-0 invert opacity-90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.9 }}
            transition={{ duration: 1.2, delay: 0.2 }}
          />

          <motion.h1
            className="text-4xl sm:text-6xl md:text-8xl font-serif font-light text-white mb-4 sm:mb-6 leading-[1.1] tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            The Sufi Way
          </motion.h1>

          <motion.p
            className="text-lg sm:text-2xl md:text-3xl font-serif font-light text-white/80 mb-8 sm:mb-10 italic"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            A weekend of stillness, connection & renewal
          </motion.p>

          <motion.p
            className="text-sm sm:text-base text-white/60 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
          >
            Find deep inner peace that lasts. Connect with your authentic self.
            Learn from true Sufi practitioners in the sacred countryside of Barcelona.
          </motion.p>

          {/* Dates */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-2 text-white/50 text-xs sm:text-sm uppercase tracking-[0.15em] mb-3">
              <span>Upcoming Retreats</span>
            </div>
            <div className="text-white text-lg sm:text-xl font-serif">
              April 17–19, 2026 &nbsp;&middot;&nbsp; May 22–24, 2026
            </div>
            <div className="text-white/50 text-sm mt-1">Barcelona, Spain</div>

            <div className="mt-6">
              <CountdownTimer targetDate={nextRetreatDate} />
              <p className="text-white/40 text-xs mt-2 tracking-wide">Until April 17th retreat</p>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="space-y-3"
          >
            <a
              href="https://www.beshnoretreats.com/product-page/the-sufi-way-a-gentle-introduction"
              className="inline-block bg-white text-stone-800 px-8 py-3.5 sm:px-10 sm:py-4 rounded-full text-sm sm:text-base font-medium hover:bg-white/90 transition-all shadow-lg"
            >
              Reserve Your Spot &mdash; &euro;100 Deposit
            </a>
            <p className="text-white/50 text-xs sm:text-sm">
              Full investment: &euro;295 (incl. 21% VAT) &middot; Payment plans available
            </p>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-5 h-5 text-white/30" />
        </motion.div>
      </section>


      {/* ─── INTRO / WHAT IS THIS ──────────────────────────── */}
      <section className="py-20 sm:py-28 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <FadeIn>
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-sage-600 mb-6">The Experience</p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800 mb-8 leading-tight">
              A gentle, immersive weekend exploring Sufi wisdom
            </h2>
            <p className="text-base sm:text-lg text-stone-500 leading-relaxed max-w-2xl mx-auto">
              Through embodied, creative and communal practices, you'll discover ancient techniques
              for inner peace — guided by authentic practitioners who've dedicated their lives to this path.
            </p>
          </FadeIn>

          {/* Key dates */}
          <FadeIn delay={0.15} className="mt-12 sm:mt-16">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100">
                <div className="text-2xl sm:text-3xl font-serif text-stone-800 mb-1">Apr 17–19</div>
                <div className="text-stone-400 text-sm">Weekend Retreat</div>
              </div>
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100">
                <div className="text-2xl sm:text-3xl font-serif text-stone-800 mb-1">May 22–24</div>
                <div className="text-stone-400 text-sm">Weekend Retreat</div>
              </div>
              <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100">
                <div className="text-2xl sm:text-3xl font-serif text-stone-800 mb-1">&euro;295</div>
                <div className="text-stone-400 text-sm">Per retreat (incl. VAT)</div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ─── FULL-WIDTH IMAGE BREAK ────────────────────────── */}
      <section className="relative h-[50vh] sm:h-[60vh] overflow-hidden">
        <img
          src="https://static.wixstatic.com/media/7b6fe5_935675ab4bfc4fd0b5e265132dc0c048~mv2.jpg/v1/fill/w_2880,h_1048,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/7b6fe5_935675ab4bfc4fd0b5e265132dc0c048~mv2.jpg"
          alt="Peaceful meditation setting"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-900/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <FadeIn className="text-center px-4">
            <blockquote className="text-2xl sm:text-4xl md:text-5xl font-serif font-light text-white leading-tight max-w-3xl italic">
              "The heart already knows. This retreat clears space to hear its whisper."
            </blockquote>
          </FadeIn>
        </div>
      </section>


      {/* ─── WHAT YOU'LL EXPERIENCE ────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-sage-600 mb-4">Your Weekend</p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
              What You'll Experience
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 sm:gap-y-14">
            {[
              {
                title: "Guided Nature Trek",
                description: "Mindful walking meditation through Barcelona's countryside, connecting deeply with the natural world through contemplative practices."
              },
              {
                title: "Sufi Yoga",
                description: "Movement and breath infused with Sufi principles — incorporating sacred geometry and body awareness to harmonize body, breath, and spirit."
              },
              {
                title: "Live Sufi Music",
                description: "Sacred Sufi music tradition. Hauntingly beautiful melodies that open pathways to deeper states of consciousness."
              },
              {
                title: "Hadra Circle",
                description: "Ancient practice of sacred movement and chanting. Feel the power of community as you move together in rhythm and remembrance."
              },
              {
                title: "Reflection & Journaling",
                description: "Guided journaling to process insights. Share in small groups to feel truly seen and supported on your journey."
              },
              {
                title: "Group Sharing",
                description: "Safe, supportive spaces to share your journey. Experience the healing power of being truly heard and witnessed."
              }
            ].map((activity, index) => (
              <FadeIn key={index} delay={index * 0.08}>
                <div className="group">
                  <div className="w-10 h-[1px] bg-sage-400 mb-5 group-hover:w-16 transition-all duration-300" />
                  <h3 className="text-xl sm:text-2xl font-serif text-stone-800 mb-3">{activity.title}</h3>
                  <p className="text-stone-500 text-sm sm:text-base leading-relaxed">{activity.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>


      {/* ─── PHOTO GALLERY ─────────────────────────────────── */}
      <PhotoGallery />


      {/* ─── WHY THIS WORKS ────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-sage-600 mb-4">Why This Works</p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800 mb-6">
              Ancient wisdom, lasting transformation
            </h2>
            <p className="text-base sm:text-lg text-stone-500 max-w-2xl mx-auto leading-relaxed">
              This isn't another "spiritual experience." This is proven ancient wisdom
              guided by authentic practitioners who've dedicated their lives to this path.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {[
              { title: "Find Deep Inner Peace", desc: "Not temporary relaxation — permanent peace that stays with you long after the retreat ends." },
              { title: "Connect with Your True Self", desc: "Discover who you really are beneath the noise, stress, and expectations of daily life." },
              { title: "Learn Authentic Practices", desc: "800-year-old techniques that actually work, taught by real practitioners with unbroken lineage." },
              { title: "Join a Sacred Community", desc: "Connect with other seekers on the same profound journey of self-discovery and growth." }
            ].map((benefit, index) => (
              <FadeIn key={index} delay={index * 0.1}>
                <div className="bg-sage-50 rounded-xl p-6 sm:p-8">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-sage-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-lg font-serif text-stone-800 mb-2">{benefit.title}</h4>
                      <p className="text-stone-500 text-sm leading-relaxed">{benefit.desc}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>


      {/* ─── YOUR GUIDES ───────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-sand">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-sage-600 mb-4">Your Guides</p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
              Authentic Sufi Practitioners
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
            {/* Danish */}
            <FadeIn>
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-stone-100">
                <img
                  src="https://static.wixstatic.com/media/7b6fe5_13f0e720411844c1a14ae0da85757908~mv2.jpg/v1/fill/w_806,h_668,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202025-05-10%20at%2010_47_edited.jpg"
                  alt="Syed Danish Suhail"
                  className="w-28 h-28 sm:w-32 sm:h-32 rounded-full mx-auto mb-6 object-cover"
                />
                <h3 className="text-2xl font-serif text-stone-800 mb-1 text-center">Syed Danish Suhail</h3>
                <p className="text-sage-600 text-sm text-center mb-5">Founder, Beshno Retreats</p>
                <p className="text-stone-500 text-sm leading-relaxed mb-5">
                  Deeply rooted in the Chishti Sufi tradition, known for its emphasis on love, tolerance,
                  and service. Danish weaves ancient spiritual values into modern retreat experiences.
                </p>
                <blockquote className="border-l-2 border-sage-300 pl-4 text-stone-400 text-sm italic">
                  "My mission is to guide others on a journey that empowers them to increase their empathy
                  and understanding, both within themselves and in their relationships with others."
                </blockquote>
              </div>
            </FadeIn>

            {/* Abd al-Wadud */}
            <FadeIn delay={0.15}>
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-stone-100">
                <img
                  src="https://static.wixstatic.com/media/7b6fe5_08f770fea4e141a4aef2d5223208bd05~mv2.jpeg/v1/fill/w_726,h_602,al_c,q_85,enc_avif,quality_auto/WhatsApp%20Image%202025-06-06%20at%202_50_15%20PM.jpeg"
                  alt="Abd al-Wadud Sabaté"
                  className="w-28 h-28 sm:w-32 sm:h-32 rounded-full mx-auto mb-6 object-cover"
                />
                <h3 className="text-2xl font-serif text-stone-800 mb-1 text-center">Abd al-Wadud Sabaté</h3>
                <p className="text-sage-600 text-sm text-center mb-5">Naqshbandi Order &middot; Centre Sufi de Barcelona</p>
                <p className="text-stone-500 text-sm leading-relaxed mb-5">
                  Decades of experience guiding seekers on the authentic Sufi path. Known for his gentle presence
                  and profound wisdom in teaching spiritual awakening and self-knowledge.
                </p>
                <blockquote className="border-l-2 border-sage-300 pl-4 text-stone-400 text-sm italic">
                  "A rare opportunity to learn directly from a living practitioner rooted in tradition,
                  opening doors to spiritual connection for all who attend."
                </blockquote>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>


      {/* ─── WHAT'S INCLUDED (ACCORDION) ───────────────────── */}
      <section className="py-20 sm:py-28 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <FadeIn className="text-center mb-14 sm:mb-20">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-sage-600 mb-4">What's Included</p>
            <h2 className="text-3xl sm:text-5xl font-serif font-light text-stone-800">
              Everything for your journey
            </h2>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-stone-100">
              {includedItems.map((item, index) => (
                <AccordionItem
                  key={index}
                  item={item}
                  isOpen={openAccordion === index}
                  onToggle={() => setOpenAccordion(openAccordion === index ? null : index)}
                />
              ))}
            </div>
          </FadeIn>

          {/* Price */}
          <FadeIn delay={0.2} className="mt-12 text-center">
            <div className="bg-stone-800 text-white rounded-xl p-8 sm:p-10">
              <div className="text-3xl sm:text-5xl font-serif font-light mb-2">&euro;295</div>
              <div className="text-white/60 text-sm mb-4">per retreat, including 21% VAT</div>
              <div className="w-12 h-[1px] bg-white/20 mx-auto mb-4" />
              <p className="text-white/50 text-sm max-w-md mx-auto">
                Secure your spot with a non-refundable &euro;100 deposit. Payment plans available.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ─── TESTIMONIALS ──────────────────────────────────── */}
      <TestimonialsSection />


      {/* ─── WEEK-LONG RETREAT PROMO ───────────────────────── */}
      <section className="py-20 sm:py-28 bg-stone-800 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 items-center">
            <FadeIn>
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src="https://static.wixstatic.com/media/11062b_0bf57831f109491aba0974490795c48d~mv2.jpeg/v1/fill/w_1074,h_1058,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/11062b_0bf57831f109491aba0974490795c48d~mv2.jpeg"
                  alt="Retreat setting"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-stone-900/80 to-transparent p-6">
                  <div className="text-white/80 text-sm">March 22–28, 2027</div>
                  <div className="text-white text-lg font-serif">7 Days of Deep Transformation</div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white/40 mb-4">Something Deeper</p>
              <h2 className="text-3xl sm:text-4xl font-serif font-light mb-6 leading-tight">
                Whispers of the Heart
              </h2>
              <p className="text-white/60 leading-relaxed mb-8">
                A 7-day Sufi retreat of inner stillness, sacred connection and soulful renewal.
                If your soul is calling for something deeper, this immersive journey awaits.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  "Daily Sufi Meditations & Zikr",
                  "Creative Expression & Art Therapy",
                  "Sama & Sufi Music Performances",
                  "Nature Immersion & Equestrian Therapy",
                  "Sufi Cuisine & Cooking Workshops",
                  "Poetry Nights & Sacred Storytelling"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 text-white/70 text-sm">
                    <div className="w-1 h-1 bg-white/40 rounded-full" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="/march-retreat"
                className="inline-flex items-center gap-2 text-white border border-white/30 px-6 py-3 rounded-full text-sm hover:bg-white/10 transition-all"
              >
                Learn More <ArrowUpRight className="w-4 h-4" />
              </a>
            </FadeIn>
          </div>
        </div>
      </section>


      {/* ─── FINAL CTA ─────────────────────────────────────── */}
      <section className="py-24 sm:py-32 bg-sage-700 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <FadeIn>
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-serif font-light mb-6 leading-tight">
              Come as you are.
            </h2>
            <p className="text-xl sm:text-2xl font-serif font-light text-white/70 mb-10 italic">
              Leave as more of who you truly are.
            </p>

            <div className="mb-10">
              <div className="text-white/50 text-xs uppercase tracking-[0.15em] mb-2">Upcoming Weekends</div>
              <div className="text-lg sm:text-xl font-serif mb-1">April 17–19 &middot; May 22–24, 2026</div>
              <div className="text-3xl sm:text-4xl font-serif font-light mb-1">&euro;295</div>
              <div className="text-white/50 text-sm">per retreat &middot; &euro;100 deposit to reserve</div>
            </div>

            <div className="space-y-4">
              <a
                href="https://wa.me/34617159090"
                className="inline-block bg-white text-sage-800 px-8 py-3.5 sm:px-10 sm:py-4 rounded-full text-sm sm:text-base font-medium hover:bg-white/90 transition-all shadow-lg"
              >
                WhatsApp Us to Reserve
              </a>
              <p className="text-white/40 text-sm">
                or call <a href="tel:+34617159090" className="text-white/60 hover:text-white underline">+34 617 159 090</a>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ─── FOOTER ────────────────────────────────────────── */}
      <footer className="bg-stone-900 text-white/50 py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <img
            src="/beshnologo.png"
            alt="Beshno Retreats"
            className="h-10 sm:h-12 w-auto mx-auto mb-6 brightness-0 invert opacity-50"
          />

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-6 text-sm">
            <a href="mailto:beshnoretreats@gmail.com" className="flex items-center gap-2 hover:text-white/80 transition-colors">
              <Mail className="w-4 h-4" />
              beshnoretreats@gmail.com
            </a>
            <a href="tel:+34617159090" className="flex items-center gap-2 hover:text-white/80 transition-colors">
              <Phone className="w-4 h-4" />
              +34 617 159 090
            </a>
          </div>

          <div className="border-t border-white/10 pt-6">
            <p className="text-xs">&copy; 2026 Beshno Retreats</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WeekendRetreat;
